import type { TFunction } from 'i18next'

/**
 * Menu labels stored in the database can be either a literal (used by tenant
 * overlays / custom menus authored from the admin UI) or an i18n marker of
 * the form `i18n:<code>` (used by seeded system items).
 *
 * The marker's `<code>` mirrors the menu item's `Code` field — dotted, e.g.
 * `intelligence.monitoring.mapaTraza`. The translation files keep these flat
 * under the `menu` namespace using `:` as the segment separator (so
 * i18next's default `.` keySeparator does not split them):
 *
 *   "menu": {
 *     "intelligence": "Inteligencia y Control",
 *     "intelligence:monitoring:mapaTraza": "Mapa de Trazabilidad"
 *   }
 *
 * If the lookup fails (missing key in current locale and in the fallback),
 * we return the raw code so the UI never blanks — easier to spot and fix.
 *
 * Future: when tenants need per-locale labels for their custom menus,
 * we'll migrate to a `MenuItemTranslation` table and the backend will
 * resolve `Label` per `Accept-Language`. At that point this helper still
 * applies — the backend just stops sending `i18n:` markers and we drop the
 * marker branch. See `memory/project_menu_i18n_strategy.md`.
 */
const MARKER = 'i18n:'

interface ResolveOptions {
  /**
   * When true, prefer the abbreviated label from the `menuShort` namespace
   * (used by top-level pills where the long descriptive name would overflow
   * the nav bar). Falls back to the regular `menu` lookup when no short
   * variant is defined for the code.
   */
  short?: boolean
}

export function resolveMenuLabel(
  label: string | null | undefined,
  t: TFunction,
  options?: ResolveOptions,
): string {
  if (!label) return ''
  if (!label.startsWith(MARKER)) return label
  const code = label.slice(MARKER.length)
  const safeCode = code.replaceAll('.', ':')

  if (options?.short) {
    const shortKey = `menuShort.${safeCode}`
    const short = t(shortKey, { defaultValue: '' })
    if (typeof short === 'string' && short.length > 0) return short
  }

  const translated = t(`menu.${safeCode}`, { defaultValue: code })
  return typeof translated === 'string' ? translated : code
}
